import React, { useState } from 'react';
import { Plus, Send, Mail, MessageCircle, Smartphone, Settings, BarChart3, Users, LayoutTemplate as Template, Eye, Edit, Trash2, Play, Pause, TestTube, Download, Upload, Filter, Search, Calendar, Clock, Target, CheckCircle, AlertTriangle } from 'lucide-react';
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Textarea } from './components/ui/textarea';
import { Badge } from './components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Switch } from './components/ui/switch';
import { Separator } from './components/ui/separator';
import { Alert, AlertDescription } from './components/ui/alert';

// Communication Dashboard Component
export const CommunicationDashboard = ({ stats, onRefresh }) => {
  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Enviados</CardTitle>
            <Send className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-mg-green">{stats?.total_sent || 0}</div>
            <p className="text-xs text-gray-500">+{stats?.sent_today || 0} hoy</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Tasa de Éxito</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-mg-green">{stats?.overall_success_rate?.toFixed(1) || 0}%</div>
            <p className="text-xs text-gray-500">De comunicaciones exitosas</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Emails Enviados</CardTitle>
            <Mail className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-mg-green">{stats?.emails_sent || 0}</div>
            <p className="text-xs text-gray-500">Tasa: {stats?.email_success_rate?.toFixed(1) || 0}%</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">SMS Enviados</CardTitle>
            <Smartphone className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-mg-green">{stats?.sms_sent || 0}</div>
            <p className="text-xs text-gray-500">Tasa: {stats?.sms_success_rate?.toFixed(1) || 0}%</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <BarChart3 className="h-5 w-5 mr-2 text-mg-green" />
              Comunicaciones por Período
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Hoy</span>
                <span className="font-medium">{stats?.sent_today || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Esta semana</span>
                <span className="font-medium">{stats?.sent_this_week || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Este mes</span>
                <span className="font-medium">{stats?.sent_this_month || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Target className="h-5 w-5 mr-2 text-mg-green" />
              Tipos de Comunicación
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 flex items-center">
                  <Mail className="h-4 w-4 mr-2" />
                  Emails
                </span>
                <span className="font-medium">{stats?.emails_sent || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 flex items-center">
                  <Smartphone className="h-4 w-4 mr-2" />
                  SMS
                </span>
                <span className="font-medium">{stats?.sms_sent || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 flex items-center">
                  <MessageCircle className="h-4 w-4 mr-2" />
                  WhatsApp
                </span>
                <span className="font-medium">{stats?.whatsapp_sent || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Communications */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Clock className="h-5 w-5 mr-2 text-mg-green" />
            Comunicaciones Recientes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {stats?.recent_communications?.map((comm, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  {comm.type === 'Email' && <Mail className="h-4 w-4 text-purple-600" />}
                  {comm.type === 'SMS' && <Smartphone className="h-4 w-4 text-orange-600" />}
                  {comm.type === 'WhatsApp' && <MessageCircle className="h-4 w-4 text-green-600" />}
                  <div>
                    <div className="font-medium text-sm">{comm.recipient_name}</div>
                    <div className="text-xs text-gray-500">{comm.type}</div>
                  </div>
                </div>
                <div className="text-right">
                  <Badge className={`text-xs ${
                    comm.status === 'Sent' || comm.status === 'Delivered' ? 'bg-green-100 text-green-800' :
                    comm.status === 'Failed' ? 'bg-red-100 text-red-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {comm.status}
                  </Badge>
                  <div className="text-xs text-gray-500 mt-1">
                    {new Date(comm.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>
            )) || (
              <div className="text-center text-gray-500 py-4">
                No hay comunicaciones recientes
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Communication Templates Component
export const CommunicationTemplates = ({ 
  templates, 
  onCreateTemplate, 
  onEditTemplate, 
  onDeleteTemplate,
  onTestTemplate,
  currentUser 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterCommType, setFilterCommType] = useState('');

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = !filterType || filterType === 'all' || template.template_type === filterType;
    const matchesCommType = !filterCommType || filterCommType === 'all' || template.communication_type === filterCommType;
    return matchesSearch && matchesType && matchesCommType;
  });

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Email': return <Mail className="h-4 w-4" />;
      case 'SMS': return <Smartphone className="h-4 w-4" />;
      case 'WhatsApp': return <MessageCircle className="h-4 w-4" />;
      default: return <Send className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Plantillas de Comunicación</h2>
          <p className="text-gray-600">Gestiona plantillas para emails, SMS y WhatsApp</p>
        </div>
        {currentUser?.role === 'Admin' && (
          <Button onClick={onCreateTemplate} className="bg-mg-green hover:bg-mg-green/90">
            <Plus className="h-4 w-4 mr-2" />
            Nueva Plantilla
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Buscar plantillas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger>
                <SelectValue placeholder="Tipo de plantilla" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los tipos</SelectItem>
                <SelectItem value="Lead Welcome">Bienvenida de Lead</SelectItem>
                <SelectItem value="Quote Generated">Cotización Generada</SelectItem>
                <SelectItem value="Renewal Reminder">Recordatorio de Renovación</SelectItem>
                <SelectItem value="Task Assignment">Asignación de Tarea</SelectItem>
                <SelectItem value="Custom">Personalizada</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterCommType} onValueChange={setFilterCommType}>
              <SelectTrigger>
                <SelectValue placeholder="Tipo de comunicación" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="Email">Email</SelectItem>
                <SelectItem value="SMS">SMS</SelectItem>
                <SelectItem value="WhatsApp">WhatsApp</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredTemplates.map((template) => (
          <Card key={template.id} className={`${!template.is_active ? 'opacity-60' : ''}`}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-2">
                  {getTypeIcon(template.communication_type)}
                  <div>
                    <CardTitle className="text-lg">{template.name}</CardTitle>
                    <CardDescription>{template.description}</CardDescription>
                  </div>
                </div>
                <div className="flex items-center space-x-1">
                  <Badge variant="outline" className="text-xs">
                    {template.template_type}
                  </Badge>
                  {template.is_system && (
                    <Badge variant="secondary" className="text-xs">
                      Sistema
                    </Badge>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="text-sm text-gray-600">
                  <strong>Tipo:</strong> {template.communication_type}
                </div>
                {template.subject && (
                  <div className="text-sm text-gray-600">
                    <strong>Asunto:</strong> {template.subject}
                  </div>
                )}
                <div className="text-sm text-gray-600">
                  <strong>Variables:</strong> {template.variables?.length || 0} disponibles
                </div>
                <div className="text-sm text-gray-600">
                  <strong>Estado:</strong> {template.is_active ? 'Activa' : 'Inactiva'}
                </div>
                
                {/* Actions */}
                <div className="flex justify-between items-center pt-2">
                  <div className="flex space-x-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => onTestTemplate(template)}
                    >
                      <TestTube className="h-3 w-3 mr-1" />
                      Probar
                    </Button>
                  </div>
                  {currentUser?.role === 'Admin' && !template.is_system && (
                    <div className="flex space-x-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => onEditTemplate(template)}
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => onDeleteTemplate(template.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredTemplates.length === 0 && (
        <Card>
          <CardContent className="text-center py-12">
            <Template className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No se encontraron plantillas</h3>
            <p className="text-gray-600 mb-4">
              {searchTerm || filterType || filterCommType 
                ? 'Intenta ajustar los filtros de búsqueda'
                : 'Crea tu primera plantilla de comunicación'
              }
            </p>
            {currentUser?.role === 'Admin' && (
              <Button onClick={onCreateTemplate} className="bg-mg-green hover:bg-mg-green/90">
                <Plus className="h-4 w-4 mr-2" />
                Nueva Plantilla
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Communication Logs Component
export const CommunicationLogs = ({ logs, onRefresh }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');

  const filteredLogs = logs.filter(log => {
    const matchesSearch = log.recipient_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         log.recipient_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         log.subject?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !filterStatus || filterStatus === 'all' || log.status === filterStatus;
    const matchesType = !filterType || filterType === 'all' || log.communication_type === filterType;
    return matchesSearch && matchesStatus && matchesType;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'Sent':
      case 'Delivered':
        return 'bg-green-100 text-green-800';
      case 'Failed':
      case 'Bounced':
        return 'bg-red-100 text-red-800';
      case 'Pending':
      case 'Queued':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Email': return <Mail className="h-4 w-4 text-purple-600" />;
      case 'SMS': return <Smartphone className="h-4 w-4 text-orange-600" />;
      case 'WhatsApp': return <MessageCircle className="h-4 w-4 text-green-600" />;
      default: return <Send className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Historial de Comunicaciones</h2>
          <p className="text-gray-600">Registro de todas las comunicaciones enviadas</p>
        </div>
        <Button onClick={onRefresh} variant="outline">
          <Clock className="h-4 w-4 mr-2" />
          Actualizar
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Buscar comunicaciones..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger>
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                <SelectItem value="Pending">Pendiente</SelectItem>
                <SelectItem value="Sent">Enviado</SelectItem>
                <SelectItem value="Delivered">Entregado</SelectItem>
                <SelectItem value="Failed">Fallido</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger>
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los tipos</SelectItem>
                <SelectItem value="Email">Email</SelectItem>
                <SelectItem value="SMS">SMS</SelectItem>
                <SelectItem value="WhatsApp">WhatsApp</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Communications List */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left py-3 px-6 font-medium text-gray-900">Destinatario</th>
                  <th className="text-left py-3 px-6 font-medium text-gray-900">Tipo</th>
                  <th className="text-left py-3 px-6 font-medium text-gray-900">Asunto/Contenido</th>
                  <th className="text-left py-3 px-6 font-medium text-gray-900">Estado</th>
                  <th className="text-left py-3 px-6 font-medium text-gray-900">Fecha</th>
                  <th className="text-left py-3 px-6 font-medium text-gray-900">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredLogs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="py-4 px-6">
                      <div>
                        <div className="font-medium text-gray-900">
                          {log.recipient_name || 'N/A'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {log.recipient_email || log.recipient_phone}
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center space-x-2">
                        {getTypeIcon(log.communication_type)}
                        <span className="text-sm">{log.communication_type}</span>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="max-w-xs">
                        {log.subject && (
                          <div className="font-medium text-sm text-gray-900 truncate">
                            {log.subject}
                          </div>
                        )}
                        <div className="text-sm text-gray-500 truncate">
                          {log.content.substring(0, 100)}...
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <Badge className={`text-xs ${getStatusColor(log.status)}`}>
                        {log.status}
                      </Badge>
                      {log.error_message && (
                        <div className="text-xs text-red-600 mt-1">
                          {log.error_message}
                        </div>
                      )}
                    </td>
                    <td className="py-4 px-6">
                      <div className="text-sm text-gray-900">
                        {new Date(log.created_at).toLocaleDateString()}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(log.created_at).toLocaleTimeString()}
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <Button variant="ghost" size="sm">
                        <Eye className="h-3 w-3" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredLogs.length === 0 && (
            <div className="text-center py-12">
              <Clock className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No hay comunicaciones</h3>
              <p className="text-gray-600">
                {searchTerm || filterStatus || filterType 
                  ? 'No se encontraron comunicaciones con los filtros aplicados'
                  : 'Las comunicaciones enviadas aparecerán aquí'
                }
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Communication Settings Component
export const CommunicationSettings = ({ 
  settings, 
  onUpdateSettings,
  currentUser 
}) => {
  const [formData, setFormData] = useState({
    email_enabled: settings?.email_enabled || false,
    sendgrid_api_key: settings?.sendgrid_api_key || '',
    sender_email: settings?.sender_email || '',
    sender_name: settings?.sender_name || 'CRM INS PRO',
    sms_enabled: settings?.sms_enabled || false,
    twilio_account_sid: settings?.twilio_account_sid || '',
    twilio_auth_token: settings?.twilio_auth_token || '',
    twilio_phone_number: settings?.twilio_phone_number || '',
    whatsapp_enabled: settings?.whatsapp_enabled || false,
    whatsapp_api_key: settings?.whatsapp_api_key || '',
    whatsapp_phone_number: settings?.whatsapp_phone_number || '',
    enable_automations: settings?.enable_automations || true,
    business_hours_enabled: settings?.business_hours_enabled || true,
    business_start_hour: settings?.business_start_hour || 8,
    business_end_hour: settings?.business_end_hour || 18,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onUpdateSettings(formData);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (currentUser?.role !== 'Admin') {
    return (
      <div className="text-center py-12">
        <Settings className="h-12 w-12 mx-auto text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Acceso Restringido</h3>
        <p className="text-gray-600">Solo los administradores pueden acceder a la configuración.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Configuración de Comunicaciones</h2>
        <p className="text-gray-600">Configura los servicios de email, SMS y WhatsApp</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Email Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Mail className="h-5 w-5 mr-2 text-mg-green" />
              Configuración de Email (SendGrid)
            </CardTitle>
            <CardDescription>
              Configura SendGrid para el envío de emails automáticos
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center space-x-2">
              <Switch
                checked={formData.email_enabled}
                onCheckedChange={(checked) => handleChange('email_enabled', checked)}
              />
              <Label>Habilitar envío de emails</Label>
            </div>
            
            {formData.email_enabled && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="sendgrid_api_key">SendGrid API Key</Label>
                    <Input
                      id="sendgrid_api_key"
                      type="password"
                      value={formData.sendgrid_api_key}
                      onChange={(e) => handleChange('sendgrid_api_key', e.target.value)}
                      placeholder="SG.xxxxxxxx"
                    />
                  </div>
                  <div>
                    <Label htmlFor="sender_email">Email del Remitente</Label>
                    <Input
                      id="sender_email"
                      type="email"
                      value={formData.sender_email}
                      onChange={(e) => handleChange('sender_email', e.target.value)}
                      placeholder="noreply@mginsgroupsolutions.com"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="sender_name">Nombre del Remitente</Label>
                  <Input
                    id="sender_name"
                    value={formData.sender_name}
                    onChange={(e) => handleChange('sender_name', e.target.value)}
                    placeholder="CRM INS PRO"
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* SMS Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Smartphone className="h-5 w-5 mr-2 text-mg-green" />
              Configuración de SMS (Twilio)
            </CardTitle>
            <CardDescription>
              Configura Twilio para el envío de SMS automáticos
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center space-x-2">
              <Switch
                checked={formData.sms_enabled}
                onCheckedChange={(checked) => handleChange('sms_enabled', checked)}
              />
              <Label>Habilitar envío de SMS</Label>
            </div>
            
            {formData.sms_enabled && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="twilio_account_sid">Twilio Account SID</Label>
                  <Input
                    id="twilio_account_sid"
                    type="password"
                    value={formData.twilio_account_sid}
                    onChange={(e) => handleChange('twilio_account_sid', e.target.value)}
                    placeholder="ACxxxxxxxx"
                  />
                </div>
                <div>
                  <Label htmlFor="twilio_auth_token">Twilio Auth Token</Label>
                  <Input
                    id="twilio_auth_token"
                    type="password"
                    value={formData.twilio_auth_token}
                    onChange={(e) => handleChange('twilio_auth_token', e.target.value)}
                    placeholder="Auth Token"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="twilio_phone_number">Número de Teléfono Twilio</Label>
                  <Input
                    id="twilio_phone_number"
                    value={formData.twilio_phone_number}
                    onChange={(e) => handleChange('twilio_phone_number', e.target.value)}
                    placeholder="+1234567890"
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* WhatsApp Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <MessageCircle className="h-5 w-5 mr-2 text-mg-green" />
              Configuración de WhatsApp
            </CardTitle>
            <CardDescription>
              Configura WhatsApp Business API (Próximamente)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center space-x-2">
              <Switch
                checked={formData.whatsapp_enabled}
                onCheckedChange={(checked) => handleChange('whatsapp_enabled', checked)}
                disabled
              />
              <Label className="text-gray-400">Habilitar WhatsApp (Próximamente)</Label>
            </div>
          </CardContent>
        </Card>

        {/* General Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Settings className="h-5 w-5 mr-2 text-mg-green" />
              Configuración General
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center space-x-2">
              <Switch
                checked={formData.enable_automations}
                onCheckedChange={(checked) => handleChange('enable_automations', checked)}
              />
              <Label>Habilitar automatizaciones</Label>
            </div>
            
            <div className="flex items-center space-x-2">
              <Switch
                checked={formData.business_hours_enabled}
                onCheckedChange={(checked) => handleChange('business_hours_enabled', checked)}
              />
              <Label>Respetar horario comercial</Label>
            </div>
            
            {formData.business_hours_enabled && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="business_start_hour">Hora de inicio</Label>
                  <Select
                    value={formData.business_start_hour.toString()}
                    onValueChange={(value) => handleChange('business_start_hour', parseInt(value))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 24 }, (_, i) => (
                        <SelectItem key={i} value={i.toString()}>
                          {i.toString().padStart(2, '0')}:00
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="business_end_hour">Hora de fin</Label>
                  <Select
                    value={formData.business_end_hour.toString()}
                    onValueChange={(value) => handleChange('business_end_hour', parseInt(value))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 24 }, (_, i) => (
                        <SelectItem key={i} value={i.toString()}>
                          {i.toString().padStart(2, '0')}:00
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Setup Instructions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <AlertTriangle className="h-5 w-5 mr-2 text-yellow-600" />
              Instrucciones de Configuración
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 text-sm">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">SendGrid:</h4>
                <ol className="list-decimal list-inside space-y-1 text-gray-600">
                  <li>Crear cuenta en sendgrid.com</li>
                  <li>Ir a Settings → API Keys → Create API Key</li>
                  <li>Seleccionar "Full Access" para permisos completos</li>
                  <li>Verificar el dominio de email en Sender Authentication</li>
                </ol>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Twilio:</h4>
                <ol className="list-decimal list-inside space-y-1 text-gray-600">
                  <li>Crear cuenta en twilio.com</li>
                  <li>Desde Console obtener Account SID y Auth Token</li>
                  <li>Comprar/configurar un número de teléfono</li>
                  <li>Verificar números de prueba en desarrollo</li>
                </ol>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button type="submit" className="bg-mg-green hover:bg-mg-green/90">
            <Settings className="h-4 w-4 mr-2" />
            Guardar Configuración
          </Button>
        </div>
      </form>
    </div>
  );
};